import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Heart } from 'lucide-react';
import axios from 'axios';
import './SiteChrome.css';

/**
 * Site footer, extracted from the marketing-site UI kit
 * (docs/design-reference/uxauditpro-v1/ui_kits/marketing_site/index.html), where
 * it existed only as inline markup with no component boundary.
 *
 * CONTENT IS UNCHANGED. All 12 link targets, all 12 anchor strings, the blurb,
 * the newsletter copy and both bottom-bar lines are carried over exactly.
 *
 * The kit's footer has four columns; ours has five, because ours has a
 * newsletter the kit does not. The design losing to the content is the rule:
 * the grid gained a column rather than the newsletter being dropped.
 *
 * THE COLUMN LABELS ARE NOT HEADINGS. They were <h4>, which put an h4 straight
 * after the homepage's closing h2 ("Ready to fix your UX?") -- a real h2 -> h4
 * skip on every render. The fix then was to raise them to <h2>, on the reasoning
 * that a heading cannot skip by going UP a level.
 *
 * That removed the skip and bought a worse problem. Measured on the built DOM,
 * 2026-08-20, across six public pages: EVERY page carried exactly four h2 at
 * 14px, and all four were these labels. On the homepage that is 8 section h2
 * plus 4 chrome h2 -- an outline claiming twelve sections where the page has
 * eight, on all 36 routes at once.
 *
 * A footer column label is not a section heading. It labels a list of links
 * inside a <footer>, which is already a contentinfo landmark and needs no
 * heading to be reachable. So they are <p> now. That ends the skip (there is no
 * heading to skip TO) and takes them out of the outline, which is where the
 * problem actually was.
 *
 * The CLASS AND EVERY PIXEL OF ITS STYLING ARE UNCHANGED -- still 14px / 500 /
 * no transform / normal tracking, still margin 0 0 var(--space-3), so a <p>'s
 * UA margin cannot show. This is a tag change and nothing else. Nothing
 * references these via aria-labelledby; checked.
 *
 * > The 14px h2 was never an eyebrow. It has no uppercase, no 700, no tracking.
 * > It looks like what it is -- a label -- and only its TAG was ever wrong.
  */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOOTER_LINKS = {
  Product: [
    { label: 'UX Auditor', to: '/ux-auditor/' },
    { label: 'MVP Validator', to: '/mvp-validator/' },
    { label: 'Pricing', to: '/pricing/' },
    { label: 'Compare', to: '/compare/' },
    // Was '/ux-auditor/', which made "Demo" a duplicate of "UX Auditor" two
    // rows above it and sent the same label to two different pages depending on
    // whether you clicked it in the nav or the footer. The nav's target wins:
    // /case-studies/ is where the example audits are.
    { label: 'Demo', to: '/case-studies/' },
    // /claude/: the connector page. Published 2026-10-05; appended so no
    // existing link moves.
    { label: 'Use in Claude', to: '/claude/' },
  ],
  Company: [
    { label: 'What Is a UX Audit?', to: '/ux-audit/' },
    { label: 'About', to: '/about/' },
    { label: 'Blog', to: '/blog/' },
    { label: 'Contact', to: '/contact/' },
  ],
  Legal: [
    { label: 'Legal', to: '/legal/' },
    { label: 'Privacy', to: '/privacy/' },
    { label: 'Terms', to: '/terms/' },
    { label: 'Security', to: '/security/' },
  ],
};

export const Footer = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle | loading | success | error | duplicate
  const [newsletterError, setNewsletterError] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterStatus('loading');
    setNewsletterError('');
    try {
      const resp = await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail.trim() });
      if (resp.data.status === 'already_subscribed') {
        setNewsletterStatus('duplicate');
      } else {
        setNewsletterStatus('success');
        setNewsletterEmail('');
      }
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <footer className="chrome-footer" data-site-chrome="footer">
      <div className="chrome-footer__inner">
        <div className="chrome-footer__brand">
          <Link to="/" className="chrome-brand">
            <span className="chrome-brand__mark" aria-hidden="true">UX</span>
            <span className="chrome-brand__word">UXAuditPro</span>
          </Link>
          <p className="chrome-footer__blurb">
            AI-powered UX audits that tell you exactly what to fix.
          </p>
        </div>

        {Object.entries(FOOTER_LINKS).map(([category, links]) => (
          <div key={category}>
            <p className="chrome-footer__label">{category}</p>
            <ul className="chrome-footer__list">
              {links.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="chrome-footerlink">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="chrome-news">
          <p className="chrome-footer__label">Newsletter</p>
          <p className="chrome-news__note">UX tips &amp; insights, weekly.</p>

          {newsletterStatus === 'success' ? (
            <div className="chrome-news__ok">
              <span aria-hidden="true">✓</span> You&apos;re subscribed!
            </div>
          ) : newsletterStatus === 'duplicate' ? (
            <div className="chrome-news__muted">You&apos;re already subscribed.</div>
          ) : (
            <form onSubmit={handleNewsletterSubmit} className="chrome-news__form">
              <label className="chrome-news__muted" htmlFor="footer-newsletter-email" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
                Email address
              </label>
              <input
                id="footer-newsletter-email"
                className="chrome-field"
                type="email"
                value={newsletterEmail}
                onChange={(e) => { setNewsletterEmail(e.target.value); setNewsletterStatus('idle'); }}
                placeholder="you@example.com"
                required
              />
              <button type="submit" className="chrome-news__submit" disabled={newsletterStatus === 'loading'}>
                {newsletterStatus === 'loading'
                  ? 'Subscribing…'
                  : <><Send size={15} aria-hidden="true" /> Subscribe</>}
              </button>
              {newsletterStatus === 'error' && (
                <p className="chrome-news__err">{newsletterError}</p>
              )}
            </form>
          )}
        </div>
      </div>

      <div className="chrome-footer__bottom">
        <div className="chrome-footer__rule" />
        <div className="chrome-footer__meta">
          <p>© {new Date().getFullYear()} UXAuditPro. All rights reserved.</p>
          {/* RESTORED 2026-08-26, by the owner who cut it.
              A near-identical line was removed on 2026-08-19 on the grounds
              that it "adds nothing and was only ever there to carry the glyph".
              This one carries an attribution that the old one did not, which is
              the reason it is back rather than a reversal of the reasoning.

              THE HEART IS AN ICON, NOT AN EMOJI, and that part is not
              negotiable by copy. `styles/__tests__/no-emoji.test.js` names this
              exact line as one of the four original violations. An emoji is a
              brand mark the brand does not control — colour glyph here,
              monochrome outline there, tofu somewhere else — inside a system
              where every other shape is a token. lucide's Heart is an inline
              SVG that inherits currentColor and renders identically anywhere.

              aria-hidden with the word "heart" NOT in the accessible name: a
              screen reader gets "Made for product teams everywhere. Powered by
              Roars", which is the sentence. Announcing the decoration would
              make it worse, not more accessible. */}
          <p className="chrome-footer__made">
            Made for product teams everywhere
            <Heart
              className="chrome-footer__heart"
              aria-hidden="true"
              focusable="false"
              strokeWidth={0}
            />
            . Powered by{' '}
            <a
              href="https://www.roarsinc.com"
              target="_blank"
              rel="noopener noreferrer"
              className="chrome-footer__roars"
            >
              Roars
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
